import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Dropdown, Image, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { io } from "socket.io-client";

//SimpleBar
import SimpleBar from "simplebar-react";

import { useTranslation } from "react-i18next";
import { getDecodeId } from "slices/userProfile/thunk";
import { CallAPIGetPromise, CallAPIPostPromise } from "./commaon";
import { store } from "index";
import apiUrl from "slices/apiEndpoint";

interface NotificationItem {
  id: number;
  type: string;
  message: string;
  metadata?: any;
  created_at: string;
  is_read?: boolean | number;
}

const parseMetadata = (metadata: any) => {
  if (!metadata) return {};
  if (typeof metadata === "object") return metadata;
  try {
    return JSON.parse(metadata);
  } catch (_error) {
    return {};
  }
};

const NotificationDropdown = () => {
  const { t } = useTranslation();
  const userId = getDecodeId();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [attentionNotification, setAttentionNotification] =
    useState<NotificationItem | null>(null);
  const [attentionVersion, setAttentionVersion] = useState(0);
  const attentionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const unreadCount = notifications.filter(
    (notification) => !Boolean(notification.is_read),
  ).length;
  useEffect(() => {
    if (userId) {
      CallAPIGetPromise(`/notification-center/getNotifications/${userId}`)
        .then((res: any) => {
          setNotifications(Array.isArray(res) ? res : []);
        })
        .catch(() => {
          console.log("ee");
        });
    }
  }, [userId]);

  useEffect(() => {
    const token = store.getState().Login.token;

    if (!userId || !token) return;

    const socket = io(apiUrl, {
      path: "/socket.io",
      auth: { token },
      transports: ["websocket", "polling"],
    });

    socket.on("chat:notification", (notification: NotificationItem) => {
      setNotifications((current) => [
        notification,
        ...current.filter((item) => item.id !== notification.id),
      ]);
      setAttentionNotification(notification);
      setAttentionVersion((current) => current + 1);
      if (attentionTimerRef.current) clearTimeout(attentionTimerRef.current);
      attentionTimerRef.current = setTimeout(() => {
        setAttentionNotification(null);
        attentionTimerRef.current = null;
      }, 6500);
    });

    socket.on(
      "chat:notification:read",
      ({ notificationIds }: { notificationIds?: number[] }) => {
        const ids = new Set((notificationIds || []).map(Number));

        setNotifications((current) =>
          current.map((item) =>
            ids.has(Number(item.id)) ? { ...item, is_read: true } : item,
          ),
        );
        setAttentionNotification((current) =>
          current && ids.has(Number(current.id)) ? null : current,
        );
      },
    );

    return () => {
      if (attentionTimerRef.current) clearTimeout(attentionTimerRef.current);
      socket.disconnect();
    };
  }, [userId]);

  const getNotificationRoute = (notification: NotificationItem) => {
    const metadata = parseMetadata(notification.metadata);
    const projectTypes = [
      "project_created",
      "contract_created",
      "contractor_signed",
      "homeowner_signed",
    ];

    if (metadata.route || metadata.path || metadata.url) {
      const suppliedRoute = metadata.route || metadata.path || metadata.url;
      if (typeof suppliedRoute === "string" && suppliedRoute.startsWith("/"))
        return suppliedRoute;
    }
    if (projectTypes.includes(notification.type) && metadata.projectId) {
      return `/create-contract?tab=Project-Details&id=${metadata.projectId}`;
    }
    if (notification.type.includes("lead") && metadata.projectId) {
      return `/create-contract?tab=Project-Details&id=${metadata.projectId}`;
    }
    if (
      notification.type.includes("chat") ||
      notification.type.includes("message")
    )
      return "/apps-chat";
    if (
      notification.type.includes("support") ||
      notification.type.includes("ticket")
    )
      return "/apps-tickets-list";
    if (notification.type === "billing_payment_failed")
      return "/billing/payment-alerts";
    if (notification.type === "billing_payment_activity")
      return "/pages-all-transactions";
    return null;
  };

  const handleNotificationClick = (notification: NotificationItem) => {
    const wasUnread = !Boolean(notification.is_read);
    const route = getNotificationRoute(notification);

    if (route) navigate(route);
    setAttentionNotification(null);

    if (wasUnread) {
      setNotifications((current) =>
        current.map((item) =>
          item.id === notification.id ? { ...item, is_read: true } : item,
        ),
      );

      CallAPIPostPromise("/notification-center/mark-notificationAsRead", {
        notificationId: notification.id,
        userId,
      }).catch(() => {
        setNotifications((current) =>
          current.map((item) =>
            item.id === notification.id ? { ...item, is_read: false } : item,
          ),
        );
      });
    }
  };
  return (
    <React.Fragment>
      <Dropdown
        className="topbar-head-dropdown ms-1 header-item"
        id="notificationDropdown"
      >
        <Dropdown.Toggle
          id="notification"
          type="button"
          className="btn btn-icon btn-topbar btn-ghost-dark rounded-circle arrow-none"
          aria-label={t("Notifications")}
          title={t("Notifications")}
        >
          <span
            key={`bell-${attentionVersion}`}
            className={`topbar-icon-frame ${attentionNotification ? "notification-bell-attention" : ""}`}
          >
            <i className="bi bi-bell"></i>
          </span>
          {unreadCount > 0 && (
            <span
              key={`badge-${attentionVersion}`}
              className={`notification-count-badge ${attentionNotification ? "notification-count-badge--attention" : ""}`}
            >
              <span className="notification-badge">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
              <span className="visually-hidden">{t("unread messages")}</span>
            </span>
          )}
        </Dropdown.Toggle>
        <Dropdown.Menu
          className="dropdown-menu-lg dropdown-menu-end p-0 modern-dropdown modern-notification-dropdown"
          aria-labelledby="page-header-notifications-dropdown"
        >
          <div className="dropdown-head rounded-top ">
            <div className="p-3 border-bottom border-bottom-dashed">
              <Row className="align-items-center">
                <Col>
                  <h6 className="mb-0 fs-lg fw-semibold">
                    {t("Notifications")}
                  </h6>
                  {unreadCount > 0 ? (
                    <p className="fs-md text-muted mt-1 mb-0">
                      {t("You have")}{" "}
                      <span className="fw-semibold notification-unread">
                        {unreadCount}
                      </span>{" "}
                      {t("unread messages")}
                    </p>
                  ) : (
                    <div className="mt-1 ">{t("You're all caught up")}</div>
                  )}
                </Col>
              </Row>
            </div>
          </div>

          <div className="py-2 ps-2" id="notificationItemsTabContent">
            <SimpleBar style={{ maxHeight: "300px" }} className="pe-2">
              {notifications.length > 0 && (
                <h6 className="text-overflow text-muted fs-sm my-2 text-uppercase notification-title">
                  {t("Recent")}
                </h6>
              )}
              {notifications &&
                notifications[0] &&
                notifications.map((notif: any) => {
                  const metadata = parseMetadata(notif.metadata);
                  const route = getNotificationRoute(notif);
                  return (
                    <Dropdown.Item
                      key={notif.id}
                      onClick={() => handleNotificationClick(notif)}
                      className={`d-flex tailwind-prefix align-items-start notification-list-item ${!Boolean(notif.is_read) ? "notification-list-item--unread" : "notification-list-item--read"} ${route ? "notification-list-item--linked" : ""}`}
                      style={{ whiteSpace: "normal", gap: "10px" }} // spacing between pic and text
                    >
                      {/* Sender profile pic or fallback */}
                      {metadata.profilePic ? (
                        <div className="flex items-center justify-center h-[50px] w-[50px]">
                          <Image
                            src={metadata.profilePic}
                            className="h-[40px] flex items-center w-[40px] rounded-full"
                            alt="sender"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-[50px] w-[50px]">
                          <i
                            className="bi   bi-person-circle"
                            style={{ fontSize: "2rem", color: "#6c757d" }}
                          ></i>
                        </div>
                      )}

                      {/* Message content */}
                      <div
                        className="notification-list-item__content"
                        style={{
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                        }}
                      >
                        <div>{notif.message}</div>{" "}
                        {/* No truncation, will wrap naturally */}
                        <small className="text-muted">
                          {new Date(notif.created_at).toLocaleString()}
                        </small>
                      </div>
                      {!Boolean(notif.is_read) && (
                        <span
                          className="notification-unread-dot"
                          aria-label={t("Unread")}
                        ></span>
                      )}
                    </Dropdown.Item>
                  );
                })}
            </SimpleBar>

            <div className="notification-actions" id="notification-actions">
              <div className="d-flex text-muted justify-content-center align-items-center">
                {t("Select")}{" "}
                <div id="select-content" className="text-body fw-semibold px-1">
                  0
                </div>{" "}
                {t("Result")}
                <Button
                  variant="link-danger"
                  className="btn-link p-0 ms-2"
                  data-bs-toggle="modal"
                  data-bs-target="#removeNotificationModal"
                >
                  {t("Remove")}
                </Button>
              </div>
            </div>
          </div>
        </Dropdown.Menu>
      </Dropdown>
      {attentionNotification &&
        (() => {
          const metadata = parseMetadata(attentionNotification.metadata);
          return (
            <div
              key={`notice-${attentionVersion}`}
              className="notification-focus-card"
              role="status"
              aria-live="polite"
            >
              <button
                type="button"
                className="notification-focus-card__main"
                onClick={() => handleNotificationClick(attentionNotification)}
                aria-label={`${attentionNotification.message}. Open notification`}
              >
                <span className="notification-focus-card__avatar">
                  {metadata.profilePic ? (
                    <img src={metadata.profilePic} alt="" />
                  ) : (
                    <i className="bi bi-chat-dots-fill" aria-hidden="true" />
                  )}
                </span>
                <span className="notification-focus-card__copy">
                  <small>New notification</small>
                  <strong>{attentionNotification.message}</strong>
                  <span>Click to view</span>
                </span>
              </button>
              <button
                type="button"
                className="notification-focus-card__close"
                onClick={() => setAttentionNotification(null)}
                aria-label="Dismiss notification"
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            </div>
          );
        })()}
    </React.Fragment>
  );
};

export default NotificationDropdown;
